'use client'
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownSection,
  DropdownItem,
} from '@nextui-org/dropdown'

import Link from 'next/link'
import { useState } from 'react'
import { DropDownNav } from './NavBarItems.tsx/DropDownNav'
import { Hamburger } from './NavBarItems.tsx/Hamburger'
import { SearchBar } from './NavBarItems.tsx/SearchBar'
import { dropDownItems, navItems } from '@/lib/util/translations'

const supportedLanguages = ['en', 'fi', 'nb']

const getLanguageName = (locale: string) => {
  const displayNames = {
    en: 'English',
    nb: 'Norsk',
    fi: 'Suomi',
  }
  return displayNames[locale as keyof typeof displayNames]
}

interface Props {
  locale: string
  slugTranslations: {
    [key: string]: string
  }
}

export function Navbar(props: Props) {
  const { locale, slugTranslations } = props
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false)
  return (
    <div className="relative bg-white border-b border-green-100 z-10">
      <div className="flex items-center justify-between h-20 px-4 md:px-8 mx-auto max-w-[var(--max-width)]">
        <Link
          href={locale ? `/lang/${locale}` : '/lang/en'}
          className="flex gap-4 items-center w-60"
          title="Back to front page"
        >
          <img src="/logo.jpg" alt="A-DVICE" />
        </Link>
        <Hamburger
          open={isMenuOpen}
          clickHandler={() => setIsMenuOpen(!isMenuOpen)}
        />
        <div
          className={`absolute lg:relative right-0 lg:top-auto top-20 w-full lg:w-auto transition-all ease-out duration-300 ${
            isMenuOpen ? 'block' : 'hidden'
          } lg:flex flex-col lg:flex-row gap-0 font-medium items-center bg-white shadow-lg lg:shadow-none`}
        >
          <ul className="flex-col lg:flex-row flex lg:justify-between lg:gap-8 gap-4 font-medium items-center w-full">
            <li className="w-full">{locale === 'en' && <SearchBar />}</li>
            {navItems[locale as keyof typeof navItems].map((item) => (
              <li
                key={item.href}
                className="w-full md:w-auto relative hover:bg-gray-100 flex-shrink-0"
              >
                <div
                  className="flex items-center  justify-between"
                  key={item.href}
                >
                  <Link
                    href={item.href}
                    className={`block px-4 py-3 w-full text-center`}
                  >
                    {item.name}
                  </Link>
                </div>
              </li>
            ))}
            <li className="w-full md:w-auto relative hover:bg-gray-100">
              <div className="flex items-center justify-between whitespace-nowrap">
                <DropDownNav
                  menuOpen={isMenuOpen}
                  subMenuOpen={isSubMenuOpen}
                  setSubMenu={setIsSubMenuOpen}
                >
                  {locale === 'en'
                    ? 'Learn More'
                    : locale === 'fi'
                      ? 'Lisätietoa'
                      : 'Lær mer'}
                </DropDownNav>
                <div
                  className={`absolute top-full mt-1 bg-white shadow-md ${
                    isSubMenuOpen ? 'block' : 'hidden'
                  }`}
                  style={{
                    minWidth: 'max-content', // Ensures the dropdown grows to fit the longest text
                    right: 0, // Aligns the dropdown's right edge to the "Learn More" button
                  }}
                >
                  {dropDownItems[locale as keyof typeof dropDownItems].map(
                    (page) => (
                      <Link
                        key={page.name}
                        onClick={() => {
                          setIsMenuOpen(false)
                          setIsSubMenuOpen(false)
                        }}
                        href={page.href}
                        className={`block px-4 py-3 w-full text-left hover:bg-gray-100`}
                      >
                        {page.name}
                      </Link>
                    ),
                  )}
                </div>
              </div>
            </li>

            <li className="w-full">
              <Dropdown className="w-full">
                <DropdownTrigger>
                  <button className="block px-4 py-3 w-full text-center hover:bg-gray-100">
                    {getLanguageName(locale)}
                  </button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label="Static Actions"
                  className="block w-full"
                >
                  {supportedLanguages
                    .filter((lang) => lang !== locale)
                    .map((lang) =>
                      slugTranslations &&
                      Object.keys(slugTranslations).includes(lang) ? (
                        <DropdownItem
                          className={`bg-white block px-4 py-3 w-full text-left hover:bg-gray-100`}
                          key={lang}
                          href={`/${Object.keys(slugTranslations).includes(lang) ? slugTranslations[lang] : slugTranslations.en}`}
                        >
                          {getLanguageName(lang)}
                        </DropdownItem>
                      ) : (
                        <DropdownItem
                          className="block px-4 py-3 w-full text-left cursor-not-allowed bg-gray-50 pointer-events-none"
                          key={lang}
                        >
                          No {getLanguageName(lang)} translation available
                        </DropdownItem>
                      ),
                    )}
                </DropdownMenu>
              </Dropdown>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
